/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {AutoResizingInputEl} from 'common/components/autoresizing-input';
import {BannerEl} from 'common/components/banner';
import {BannerTheme, BannerType} from 'common/components/banner/types';
import {BreadcrumbsEl, BreadcrumbsTheme} from 'common/components/breadcrumbs';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {ButtonBarEl} from 'common/components/button-bar';
import {CheckboxEl} from 'common/components/checkbox';
import {ClickableInputEl} from 'common/components/clickable-input';
import {DropMenuEl} from 'common/components/drop-menu';
import {FileUploaderEl} from 'common/components/file-uploader';
import {InputEl} from 'common/components/input';
import {InputGroupEl} from 'common/components/input-group';
import {LastUpdatedEl} from 'common/components/last-updated';
import {ProgressBarEl} from 'common/components/progress-bar';
import {RadioButtonEl} from 'common/components/radio-button';
import {RevealableTextEditEl} from 'common/components/revealable-text-editor';
import {SelectEl} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SettingsSectionEl} from 'common/components/settings-section';
import {SettingsLiEl} from 'common/components/settings-section/settings-li';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerSize, SpinnerTheme} from 'common/components/spinner/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {SwitchEl} from 'common/components/switch';
import {TagEl} from 'common/components/tag';
import {TagSelectorEl} from 'common/widgets/tag-selector';
import {TextHighlightEl} from 'common/components/text-highlight';
import COLORS from 'common/stylesheets/mixins/colors.json';
import {CopyToClipboardEl} from 'common/widgets/copy-to-clipboard';
import {InputType} from 'common/components/settings-section/settings-li/types';

import {AutoDocEl} from '../components/auto-doc';
import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function FormsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Forms and Form Elements'>
      <SubSection title='Forms'>
        <h3>Field and Form Validation</h3>
        <p sel='.desc'>We have three ways of dealing with validation: Live, Delayed, and Server-side.</p>

        <h4>Live Field Validation</h4>
        <div sel='.form-container'>
          <InputEl
            attrs={{
              'field-id': `name`,
              label: `Name`,
              placeholder: `e.g Mary Smith`,
              necessary: `required`,
              'validation-status': `invalid`,
              'error-message': `Invalid Character`,
            }}
            props={{value: `Mark>>`}}
          />
        </div>
        <p sel='.desc'>
          Live validation is only useful in situations in which an immediate error is discovered like an invalid
          character.
        </p>

        <h4>Delayed Field Validation</h4>
        <div sel='.form-container'>
          <InputEl
            attrs={{
              'field-id': `Password`,
              type: `password`,
              label: `Password`,
              necessary: `required`,
              'validation-status': `invalid`,
              'error-message': `Must be at least 6 characters in length.`,
            }}
            props={{value: `pass`}}
          />
        </div>
        <p sel='.desc'>
          This should be our most common use of field validation, as we want to give users the opportunity input
          correctly rather than assuming they are actively making a mistake.
        </p>

        <h4>Server-side Form Validation</h4>
        <div sel='.form-container'>
          <BannerEl
            attrs={{
              theme: BannerTheme.Warn,
              type: BannerType.Embedded,
              'body-text': `This email address is already in use. Use another email address or Login.`,
            }}
          />
          <div sel='.form-header'>Sign Up</div>
          <InputEl
            attrs={{
              'field-id': `Name`,
              label: `Name`,
              placeholder: `Eleanor Shellstrop`,
              necessary: `required`,
            }}
            props={{value: `Mark`}}
          />
          <InputEl
            attrs={{
              'field-id': `Email`,
              label: `Email`,
              necessary: `required`,
              'validation-status': `invalid`,
            }}
            props={{value: `mark@gmail.com`}}
          />
          <InputEl
            attrs={{
              'field-id': `Password`,
              type: `password`,
              label: `Password`,
              necessary: `required`,
            }}
          />
          <InputEl
            attrs={{
              'field-id': `Repeat Password`,
              type: `password`,
              label: `Repeat Password`,
              necessary: `required`,
            }}
          />
          <ButtonEl
            attrs={{
              size: ButtonSize.Large,
              theme: ButtonTheme.Primary,
              role: `button`,
            }}
          >
            Sign Up
          </ButtonEl>
        </div>
        <p sel='.desc'>
          There are plenty of situations which require a server call before notifying users of a form’s status. This
          calls for the use of Small or Medium embedded banners in order to call out the fields which are proving
          troublesome.
        </p>

        <div sel='.divider-line' />

        <h3>Full Form Examples</h3>
        <p sel='.desc'>Use the following examples as guidance of what to do and not do.</p>

        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Use a consecutive series of identical length forms in a modal or container to create a form.`,
          }}
        />
        <div sel='.flex-container'>
          <div sel='.flex-element'>
            <div sel='.form-container'>
              <div sel='.form-header'>Sign Up</div>
              <InputEl
                attrs={{
                  'field-id': `Name`,
                  label: `Name`,
                  placeholder: `Eleanor Shellstrop`,
                  necessary: `required`,
                }}
                props={{value: `Mark`}}
              />
              <InputEl
                attrs={{
                  'field-id': `Email`,
                  label: `Email`,
                  necessary: `required`,
                }}
              />
              <InputEl
                attrs={{
                  'field-id': `Password`,
                  type: `password`,
                  label: `Password`,
                  necessary: `required`,
                }}
              />
              <InputEl
                attrs={{
                  'field-id': `Repeat Password`,
                  type: `password`,
                  label: `Repeat Password`,
                  necessary: `required`,
                }}
              />
              <ButtonEl
                attrs={{
                  size: ButtonSize.Large,
                  theme: ButtonTheme.Primary,
                  role: `button`,
                }}
              >
                Sign Up
              </ButtonEl>
            </div>
          </div>
          <div sel='.flex-element'>
            <div sel='.form-container'>
              <BannerEl
                attrs={{
                  theme: BannerTheme.Warn,
                  type: BannerType.Embedded,
                  'body-text': `This email address is already in use. Use another email address or Login.`,
                }}
              />
              <div sel='.form-header'>Sign Up</div>
              <InputEl
                attrs={{
                  'field-id': `Name`,
                  label: `Name`,
                  placeholder: `Eleanor Shellstrop`,
                  necessary: `required`,
                }}
                props={{value: `Mark`}}
              />
              <InputEl
                attrs={{
                  'field-id': `Email`,
                  label: `Email`,
                  necessary: `required`,
                  'validation-status': `invalid`,
                }}
                props={{value: `mark@gmail.com`}}
              />
              <InputEl
                attrs={{
                  'field-id': `Password`,
                  type: `password`,
                  label: `Password`,
                  necessary: `required`,
                }}
              />
              <InputEl
                attrs={{
                  'field-id': `Repeat Password`,
                  type: `password`,
                  label: `Repeat Password`,
                  necessary: `required`,
                }}
              />
              <ButtonEl
                attrs={{
                  size: ButtonSize.Large,
                  theme: ButtonTheme.Primary,
                  role: `button`,
                }}
              >
                Sign Up
              </ButtonEl>
            </div>
          </div>
        </div>

        <h4>Handling Multiple Field Errors</h4>
        <p sel='.desc'>
          When handling multiple field errors—when possible try to include both inline and embedded field validation.
          One should be a summary of the errors (Banner validation) the other should be direct field issues.
        </p>
        <div sel='.flex-container'>
          <div sel='.form-container'>
            <BannerEl
              attrs={{
                theme: BannerTheme.Warn,
                type: BannerType.Embedded,
                'header-text': `Multiple Field Issues`,
                'body-text': `• Invalid Characters in Full Name Field.
                              • Passwords do not match.`,
              }}
            />
            <div sel='.form-header'>Sign Up</div>
            <InputEl
              attrs={{
                'field-id': `Name`,
                label: `Name`,
                placeholder: `Eleanor Shellstrop`,
                necessary: `required`,
                'validation-status': `invalid`,
              }}
              props={{value: `Mark`}}
            />
            <InputEl
              attrs={{
                'field-id': `Email`,
                label: `Email`,
                necessary: `required`,
              }}
              props={{value: `mark@gmail.com`}}
            />
            <InputEl
              attrs={{
                'field-id': `Password`,
                type: `password`,
                label: `Password`,
                necessary: `required`,
              }}
              props={{value: `pass`}}
            />
            <InputEl
              attrs={{
                'field-id': `Repeat Password`,
                type: `password`,
                label: `Repeat Password`,
                necessary: `required`,
                'validation-status': `invalid`,
              }}
              props={{value: `pass2`}}
            />
            <ButtonEl
              attrs={{
                size: ButtonSize.Large,
                theme: ButtonTheme.Primary,
                role: `button`,
              }}
            >
              Sign Up
            </ButtonEl>
          </div>
        </div>

        <h4>Left to Right Forms</h4>
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not make left to right reading forms. While it’s a thing we can do, we don’t generally want to do this, we want to stick with linear top down forms that don’t require the user's eye to wander.`,
          }}
        />
        <div sel='.form-container'>
          <div sel='.form-header'>Bad Form</div>
          <InputEl
            attrs={{
              'field-id': `First Name`,
              label: `First Name`,
              placeholder: `Mark`,
              necessary: `required`,
              'style-override': `:host { margin-right: 8px; width: 146px; }`,
            }}
            props={{value: `Mark`}}
          />
          <InputEl
            attrs={{
              'field-id': `Last Name`,
              label: `Last Name`,
              placeholder: `Johnson`,
              necessary: `required`,
              'style-override': `:host { width: 146px; }`,
            }}
          />
          <InputEl
            attrs={{
              'field-id': `Email`,
              label: `Email`,
              necessary: `required`,
              'style-override': `:host { margin-right: 8px; width: 146px; }`,
            }}
            props={{value: `mark@gmail.com`}}
          />
          <InputEl
            attrs={{
              'field-id': `Password`,
              type: `password`,
              label: `Password`,
              necessary: `required`,
              'style-override': `:host { width: 146px; }`,
            }}
            props={{value: `pass`}}
          />
          <ButtonEl
            attrs={{
              size: ButtonSize.Large,
              theme: ButtonTheme.Primary,
              role: `button`,
            }}
          >
            Sign Up
          </ButtonEl>
        </div>
      </SubSection>

      <SubSection title='mp-input' autoDoc={true}>
        <p sel='.desc'>Text input with label and optional validation</p>

        <h3>The Basics</h3>

        <h4>Labels</h4>
        <p sel='.desc'>These help describe the field you're about to interact with.</p>
        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              label: `Email Address`,
            }}
          />
        </CodeTipEl>

        <h4>Title Case</h4>
        <p sel='.desc'>
          We use Title Case throughout the product for any kind of label. This is no different. We do this to treat
          objects as more formal or proper objects as opposed to ephemera.
        </p>

        <div sel='.divider-line' />

        <h3>Placeholder Text</h3>
        <p sel='.desc'>When should you use placeholder text? TDLR: It depends.</p>

        <h4>Use it for brand moments, and places where it might be helpful.</h4>
        <p sel='.desc'>
          Feel free to use placeholder text in areas of additional clarity, or for brand moment opportunities.
        </p>

        <p sel='.desc'>
          It's not wildly helpful to put email@email.com as your placeholder. It would be better to have a character a
          user might be able to follow like eleanorshellstrop@gmail.com. We’ve experienced positive reactions when we
          give subtle references to things outside of our product, in this case around the TV show The Good Place.
        </p>

        <div sel='.flex-container'>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `success`,
                type: BannerType.Embedded,
                'header-text': `Do`,
                'body-text': `Use fun, useful, references that the customer may discover.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Full Name`,
                  placeholder: `Eleanor Shellstrop`,
                }}
              />
            </div>
          </div>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `error`,
                type: BannerType.Embedded,
                'header-text': `Do Not`,
                'body-text': `There is no need to use "e.g." in placeholder text.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Full Name`,
                  placeholder: `e.g. Your Name`,
                }}
              />
            </div>
          </div>
        </div>
        <br />
        <br />

        <h4>Empty Placeholders and Hinting</h4>
        <p sel='.desc'>
          Using placeholders as hint text is also helpful to reveal a format users can expect to encounter e.g. Phone
          Numbers, Credit Card Numbers, Dates.
        </p>
        <p sel='.desc'>There are also times when an empty placeholder is beneficial, like a password entry field.</p>
        <div sel='.flex-container'>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `success`,
                type: BannerType.Embedded,
                'header-text': `Do`,
                'body-text': `Use fun, useful, references that the customer may discover.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Phone Number`,
                  placeholder: `1 (912) 555 5507`,
                }}
              />
            </div>
          </div>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `error`,
                type: BannerType.Embedded,
                'header-text': `Do Not`,
                'body-text': `There is no need to add placeholder text to a password field.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Password`,
                  placeholder: `e.g. Your Password`,
                  type: `password`,
                }}
              />
            </div>
          </div>
        </div>
        <br />
        <br />

        <h4>Field Length</h4>
        <p sel='.desc'>
          Set your input fields up to be entered as expected. You don’t need a 1200px wide input for a credit card entry
          field, it should be about the length of the content you’ll be entering.
        </p>
        <p sel='.desc'>e.g. Phone Numbers, Credit Card Numbers, Dates</p>
        <div sel='.flex-container'>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `success`,
                type: BannerType.Embedded,
                'header-text': `Do`,
                'body-text': `Use fields at least the length of the expected content.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Phone Number`,
                  placeholder: `1 (912) 555 5507`,
                }}
              />
            </div>
          </div>
          <div sel='.flex-element'>
            <BannerEl
              attrs={{
                theme: `error`,
                type: BannerType.Embedded,
                'header-text': `Do Not`,
                'body-text': `Do not make the field too small, forcing the text to overflow.`,
              }}
            />
            <div sel='.form-container'>
              <InputEl
                attrs={{
                  label: `Phone Number`,
                  placeholder: `1 (912) 555 5507`,
                  'style-override': `:host { width: 100px; }`,
                }}
              />
            </div>
          </div>
        </div>

        <br />
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not make fields for a small amount of information wildly large, they should attempate to reflect the length of context expected.`,
          }}
        />
        <InputEl
          attrs={{
            label: `Phone Number`,
            placeholder: `1 (912) 555 5507`,
            'style-override': `:host { width: 100%; }`,
          }}
        />

        <div sel='.divider-line' />

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              placeholder: `e.g John Smith`,
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              'field-id': `name`,
              label: `Invalid`,
              placeholder: `e.g Mary Smith`,
              necessary: `required`,
              'validation-status': `invalid`,
              'error-message': `Please enter your name`,
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              'field-id': `email`,
              label: `Valid`,
              placeholder: `e.g abc@def.com`,
              necessary: `optional`,
              'validation-status': `valid`,
            }}
            props={{
              value: `aida@theframework.net`,
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              label: `Count apples`,
              type: `number`,
              necessary: `required`,
              'validation-status': `valid`,
              'error-message': `How many apples are on the tree?`,
            }}
            props={{
              value: `5`,
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <InputEl
            attrs={{
              'show-clear': true,
            }}
            props={{
              value: `Hit the x to clear this value`,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-input-group' autoDoc={true}>
        <p sel='.desc'>
          The field with button combination is used when space is limited, or when the CTA is strictly associated with
          the input string.
        </p>

        <CodeTipEl sel='.example'>
          <InputGroupEl
            attrs={{placeholder: `Type a name...`, busy: designSystem.state.inputGroupSaving}}
            on={{
              submit: designSystem.helpers.handleNamerSubmit,
            }}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-autoresizing-input' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <AutoResizingInputEl
            attrs={{
              'initial-width': 150,
              placeholder: `Selected event`,
              'input-style-overrides': {
                'font-weight': `400`,
                'font-size': `16px`,
              },
            }}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-clickable-input' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ClickableInputEl
            attrs={{
              error: designSystem.state.clickableInputError,
              pattern: `(This).*`,
              placeholder: `Untitled board`,
              'select-on-focus': true,
              'min-input-width': `500px`,
              required: true,
              tabindex: 0,
            }}
            on={{save: designSystem.helpers.handleClickableInputSave}}
            props={{value: designSystem.state.clickableInputValue}}
          />
        </CodeTipEl>

        <p>This text field is disabled</p>
        <CodeTipEl sel='.example'>
          <ClickableInputEl attrs={{disabled: true}} props={{value: `This text field is disabled`}} />
        </CodeTipEl>

        <p>Field is required, max length is 60 chars, expects numbers</p>
        <CodeTipEl sel='.example'>
          <ClickableInputEl
            attrs={{
              autocomplete: `off`,
              required: true,
              maxlength: 60,
              pattern: `\\d*`,
              placeholder: `placeholder`,
              name: `mymobile`,
              tabindex: 0,
              type: `tel`,
            }}
            on={{save: designSystem.helpers.handleClickableInputSaveTel}}
            props={{value: designSystem.state.clickableInputValueTel}}
          />
        </CodeTipEl>

        <p>These are the different sizes</p>
        <CodeTipEl sel='.example'>
          <ClickableInputEl attrs={{size: `xl`}} props={{value: `Size 'xl'`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ClickableInputEl attrs={{size: `large`}} props={{value: `Size 'large'`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <ClickableInputEl attrs={{size: `medium`}} props={{value: `Size 'medium'`}} />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-copy-to-clipboard' autoDoc={true}>
        <p sel='.desc'>Copy contents of input to clipboard</p>

        <CodeTipEl sel='.example'>
          <CopyToClipboardEl
            attrs={{
              type: `text`,
              value: `Very important string`,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-revealable-text-editor' autoDoc={true}>
        <p sel='.desc'>
          A text editor that encapsulates the logic for revealing and closing of the editor and allows for flexible text
          state (when editor is not revealed) + save and cancel buttons
        </p>
        <CodeTipEl sel='.example'>
          <div sel='.text-editor-container'>
            <RevealableTextEditEl
              attrs={{
                text: `Some text for testing`,
                'add-text-placeholder': `+Add Description...`,
                placeholder: `please add description`,
              }}
              on={{
                changedText: designSystem.helpers.handleDescriptionInputChange,
              }}
            >
              <div sel='.cell-content' attrs={{slot: `content`}}>
                <div sel='.text'>Some text for testing</div>
                <div sel='.editable-btn'>
                  <ButtonEl
                    attrs={{
                      icon: `edit`,
                      theme: ButtonTheme.Tertiary,
                    }}
                  />
                </div>
              </div>
            </RevealableTextEditEl>
          </div>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-switch' autoDoc={true}>
        <p sel='.desc'>To switch on/off or true/false</p>
        <CodeTipEl sel='.example'>
          <SwitchEl
            attrs={{on: designSystem.state.switchOn}}
            on={{change: (ev) => designSystem.update({switchOn: ev.detail.on})}}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-breadcrumbs' autoDoc={true}>
        <p sel='.desc'>To chain hyperlinks together for uses such as a navigation bar</p>
        <CodeTipEl sel='.example'>
          <BreadcrumbsEl
            props={{
              linkNodes: [
                {label: `Lexicon`, url: `#lexicon`},
                {label: `Custom Properties`, url: `#custom-properties`},
                {label: `Detail`, selected: true},
              ],
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <BreadcrumbsEl
            attrs={{theme: BreadcrumbsTheme.Path}}
            props={{
              linkNodes: [
                {label: `🏡 Company`, url: `#company`},
                {label: `📥 My Stuff`, url: `#my-stuff`},
              ],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-checkbox' autoDoc={true}>
        <p sel='.desc'>When selecting many of many options</p>

        <p>Default</p>
        <CodeTipEl sel='.example'>
          <CheckboxEl
            attrs={{checked: designSystem.state.checkboxChecked, theme: designSystem.state.theme}}
            on={{click: () => designSystem.update({checkboxChecked: !designSystem.state.checkboxChecked})}}
          >
            🐙 {designSystem.state.checkboxChecked ? `Checked` : `Unchecked`}
          </CheckboxEl>
        </CodeTipEl>

        <p>
          Pallette Themes
          {[`green`, `orange`, `red`].map((theme) => (
            <CodeTipEl sel='.example'>
              <CheckboxEl attrs={{checked: true, theme}}>Checkbox {theme}</CheckboxEl>
            </CodeTipEl>
          ))}
        </p>

        <CodeTipEl sel='.example'>
          <CheckboxEl attrs={{checked: true, 'checked-color': COLORS[`$green1`]}}>🐯 Custom color</CheckboxEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <CheckboxEl attrs={{'unchecked-icon': `dash`, 'checked-color': `none`}}>🐨 Custom unchecked icon</CheckboxEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <CheckboxEl attrs={{disabled: true}}>🐼 Disabled</CheckboxEl>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <CheckboxEl attrs={{focused: true}}>🦁 Focused</CheckboxEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-radio-button'>
        <p sel='.desc'>To select one out of multiple options</p>
        <CodeTipEl sel='.example'>
          <RadioButtonEl className='radio-button' children='Tigers' />
          <RadioButtonEl className='radio-button' selected children='Panda' />
          <RadioButtonEl className='radio-button' children='Giraffe' />
          <RadioButtonEl className='radio-button' icon='android-logo' children='Lion' />
          <RadioButtonEl className='radio-button' disabled children='Disabled' />
          <RadioButtonEl className='radio-button' focused children='Focused' />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <RadioButtonEl className='radio-button' theme='lite' children='Tigers' />
          <RadioButtonEl className='radio-button' theme='lite' selected children='Panda' />
          <RadioButtonEl className='radio-button' theme='lite' disabled children='Disabled' />
          <RadioButtonEl className='radio-button' theme='lite' focused children='Focused' />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-select' autoDoc={true}>
        <p sel='.desc'>To select an item from a list of items in a dropdown. Like the html select</p>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.role'
            attrs={{
              theme: SelectTheme.Primary,
              'selected-option': designSystem.state.selectNestedSelectedOption || `Select Item`,
              'menu-width': 220,
            }}
            on={{change: (ev) => designSystem.update({selectNestedSelectedOption: ev.detail.value})}}
            props={{
              sections: designSystem.state.selectSubSections,
              contextPanelTransform: (item) => ({
                header: item.label,
                subheader: `I'm a nested item`,
                description: `Try navigating in and out with your keyboard.`,
              }),
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.role'
            attrs={{
              sections: designSystem.state.selectRoleSections,
              theme: SelectTheme.Primary,
              'selected-option': designSystem.state.selectRoleSelectedOption || `Select Role`,
              'menu-width': 200,
            }}
            on={{change: (ev) => designSystem.update({selectRoleSelectedOption: ev.detail.value})}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.range'
            attrs={{
              options: designSystem.state.selectRangeOptions,
              'selected-option': designSystem.state.selectRangeSelectedOption,
              'label-transform': `uppercase`,
            }}
            on={{change: (ev) => designSystem.update({selectRangeSelectedOption: ev.detail.value})}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.role'
            attrs={{
              sections: designSystem.state.selectRoleSections,
              'selected-option': designSystem.state.selectRoleSelectedOption || `Select Role`,
              'menu-width': 200,
            }}
            on={{change: (ev) => designSystem.update({selectRoleSelectedOption: ev.detail.value})}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.icon'
            attrs={{
              'selected-option': designSystem.state.selectIconSelectedOption || `Search Icons`,
              title: `Icon Search`,
              'back-button': true,
              'menu-width': 200,
              icon: {leftIcon: designSystem.state.selectIconSelectedOption},
              theme: SelectTheme.Tertiary,
            }}
            props={{sectionsFn: () => designSystem.state.selectIconSections}}
            on={{change: (ev) => designSystem.update({selectIconSelectedOption: ev.detail.value})}}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <SelectEl
            sel='.custom'
            attrs={{
              options: designSystem.state.selectAnimalOptions,
              'menu-width': 200,
              'caret-pos': JSON.stringify({left: `50%`}),
            }}
            on={{change: (ev) => designSystem.update({selectAnimalSelectedOption: ev.detail.value})}}
            props={{
              contextPanelTransform: (item) => ({
                header: item.label,
                subheader: `I'm an animal!'`,
                description: `I spy with my little eye, something that lives in the ocean 🌊`,
              }),
            }}
          >
            <div sel='.custom-trigger' attrs={{slot: `trigger`}}>
              {designSystem.state.selectAnimalSelectedOption}
            </div>
            <div sel='.custom-footer' attrs={{slot: `footer`}}>
              <ButtonBarEl sel='.mp-button-bar-secondary'>Create new animal</ButtonBarEl>
            </div>
          </SelectEl>
        </CodeTipEl>

        <p>Example of persisting search on close and ability to clear input</p>
        <CodeTipEl sel='.example'>
          <SelectEl
            attrs={{
              'selected-option': designSystem.state.selectIconSelectedOption || `Search icons`,
              'show-clear': true,
              'preserve-search-on-close': true,
              'back-button': true,
              'menu-width': 200,
              icon: {leftIcon: designSystem.state.selectIconSelectedOption},
            }}
            props={{sectionsFn: () => designSystem.state.selectIconSections}}
            on={{change: (ev) => designSystem.update({selectIconSelectedOption: ev.detail.value})}}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-file-uploader' autoDoc={true}>
        <p sel='.desc'>Use this to ask user to upload a file</p>
        <CodeTipEl sel='.example'>
          <FileUploaderEl
            attrs={{
              accept: `text/csv`,
              label: `Select CSV file`,
              'completed-filename': designSystem.state.filename,
              'enable-delete': true,
            }}
            on={{change: designSystem.helpers.handleFileSelected, delete: designSystem.helpers.handleFileRemoved}}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-spinner' autoDoc={true}>
        <p sel='.desc'>
          When waiting for a network response or some other task that usually completes in a few seconds. Use "large"
          for page and result loading.
        </p>
        <CodeTipEl sel='.example'>
          <SpinnerEl />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <SpinnerEl attrs={{size: SpinnerSize.Large}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <SpinnerEl attrs={{theme: SpinnerTheme.Fancy}} />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-last-updated' autoDoc={true}>
        <p sel='.desc'>
          Indicate some last updated time, such as the last time a query was fetched or updated. Auto-updates.
        </p>
        <CodeTipEl sel='.example'>
          <LastUpdatedEl attrs={{'preface-text': `Updated`}} />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-tag' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <TagEl attrs={{clickable: true, 'tag-name': `Segmentation`, removable: true}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <TagEl attrs={{clickable: true, 'tag-name': `Retention`, removable: false}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <TagEl attrs={{clickable: false, 'tag-name': `Funnels`, removable: false}} />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-text-highlight' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <TextHighlightEl attrs={{text: `123 K`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.GENERIC, text: `Generic Information`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.INFORMATION, text: `Information`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.LIGHT, text: `Light`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.SUCCESS, text: `Success`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.DELETED, text: `Deleted`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.FAILED, text: `Failed`}} />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.NEUTRAL, text: `123 K`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.POSITIVE, text: `+123`}} />
          <TextHighlightEl attrs={{theme: designSystem.helpers.HighlightThemes.NEGATIVE, text: `-123`}} />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-tag-selector' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary, icon: `edit`}}
            on={{click: designSystem.helpers.toggleTagSelector}}
          >
            Edit tags
          </ButtonEl>
          <DropMenuEl
            attrs={{open: designSystem.state.open.tagSelector}}
            on={{change: designSystem.helpers.handleTagSelectorDropMenuChange}}
          >
            <div sel='.title'>
              <div sel='.header'>
                <SvgIconEl attrs={{icon: `circle-checkmark`}} />
                <div sel='.header-text'>Added to company dashboard</div>
              </div>
              <div sel='.subtext'>Viewed report by type</div>
            </div>
            <TagSelectorEl
              attrs={{
                options: designSystem.state.tagSelectorData.options,
                saving: designSystem.state.tagSelectorData.saving,
              }}
              on={{
                change: designSystem.helpers.handleTagSelectorChange,
                save: designSystem.helpers.handleTagSelectorSave,
              }}
            />
          </DropMenuEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-progress-bar' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ProgressBarEl
            attrs={{
              percent: 30,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-settings-section' autoDoc={true}>
        <p sel='.desc'>A group of related settings</p>
        <CodeTipEl sel='.example.block'>
          <SettingsSectionEl attrs={{title: `This is an optional title`}}>
            <SettingsLiEl
              attrs={{
                label: `Tags`,
                editable: true,
                type: InputType.Tag,
                'all-tags': designSystem.state.tagSelectorData.allTags,
                'selected-tags': designSystem.state.tagSelectorData.selectedTags,
                'hide-buttons': true,
              }}
              props={{
                updateFunc: designSystem.helpers.saveTags,
              }}
            />
            <SettingsLiEl
              attrs={{
                label: `Token`,
                value: `mixpanel-3`,
                editable: false,
                type: InputType.Text,
                placeholder: `Your project token`,
              }}
            />
            <SettingsLiEl
              attrs={{
                label: `E-mail`,
                value: designSystem.helpers.email(),
                editable: true,
                emptiable: true,
                placeholder: `Enter your e-mail`,
              }}
              props={{
                updateFunc: designSystem.helpers.updateEmail,
              }}
            />
            <SettingsLiEl
              attrs={{
                label: `Delete events`,
                value: `Delete all your events`,
                button: `Delete`,
                'delete-button': true,
                disabled: false,
              }}
              on={{
                'button-click': () => designSystem.helpers.openModal(`confirmDelete`),
              }}
            />
            <SettingsLiEl attrs={{label: `Link`, type: InputType.Slot}}>
              <a attrs={{href: `https://www.mixpanel.com`}}>Link to mixpanel</a>
            </SettingsLiEl>
          </SettingsSectionEl>
        </CodeTipEl>
        <AutoDocEl attrs={{component: `mp-settings-li`}} />
      </SubSection>
    </Section>
  );
}
